import React, { useContext, useEffect, useMemo } from 'react';
import ReportContentBlock from 'components/report-content/ReportContentBlock';
import { ReportContentActions } from 'app/editor/report';
import { alpha, Box, Stack } from '@mui/material';
import { prepareFormElementProps } from 'components/common/form/formTools';
import ReactHookFormController from 'components/common/form/layout/ReactHookFormController';
import { FormDataAPIType } from 'components/common/form';
import useCustomSimplifiedForm from 'components/common/form/hooks/useCustomSimplifiedForm';
import { DragDropContext, Draggable, Droppable } from 'react-beautiful-dnd';
import IconMi from 'components/common/icon/IconMi';
import { ContentDropBox } from 'components/report-content/ContentDropBox';
import {
    AllowedComponentType,
    BlockType,
    ComponentSettings,
    ContentSettings,
    DatasetField,
    ComponentSettingsManageFilters,
    IBlocksContext,
    BlocksContext,
    IKpiAlertEventFilters,
} from '@report-content';
import AlertRulesMenu from './layout/AlertRulesMenu';

export default function ReportContent({
    contentSettings,
    blocks,
    manageFiltersBlock,
    actions,
    onAddNewBlock,
    hasUserMap,
    onRestrictByUserMapChange,
    datasetFields,
    isIconDragging,
}: {
    contentSettings: ContentSettings;
    blocks: Array<BlockType<ComponentSettings>>;
    manageFiltersBlock?: BlockType<ComponentSettingsManageFilters>;
    actions: ReportContentActions;
    onAddNewBlock: (component: AllowedComponentType, index: number) => void;
    hasUserMap?: boolean;
    onRestrictByUserMapChange?: (applyUserMap: boolean) => void;
    datasetFields: Array<DatasetField>;
    isIconDragging?: boolean;
}) {
    const elementProps: FormDataAPIType = useCustomSimplifiedForm({ restrict_data_with_user_map: 'N' });

    const urlSearchParams = new URLSearchParams(window.location.search);
    const viewType = urlSearchParams.get('view_type');
    const blockUid = urlSearchParams.get('block_uid');
    const maxHeight = urlSearchParams.get('height');

    const filteredBlocks = useMemo(() => {
        if (viewType == 'tile') {
            // Show tile block
            return blocks
                .filter(
                    (block) =>
                        (blockUid != null && block.uid == blockUid) ||
                        (blockUid == null && block.component.settings.is_preview_ind == 'Y'),
                )
                .map((block) => {
                    // TODO:
                    block.component.settings.height =
                        maxHeight != null ? Number(maxHeight) - 45 : block.component.settings.height;
                    return block;
                });
        } else {
            if (contentSettings.context == 'view') {
                return blocks.filter((block) => block.component.settings.show_in_viewer == 'Y');
            }
        }
        return blocks;
    }, [blocks, viewType]);

    const restrictData = elementProps.form.hookFormWatch('restrict_data_with_user_map');
    useEffect(() => {
        if (!onRestrictByUserMapChange) {
            return;
        }
        onRestrictByUserMapChange(restrictData == 'Y');
    }, [restrictData]);

    const onDragEnd = (result: any) => {
        if (!result.destination) {
            return;
        }
        actions.sortBlocks(result.source.index, result.destination.index);
    };

    const blocksContext = useContext<IBlocksContext | null>(BlocksContext);
    if (!manageFiltersBlock) {
        blocksContext?.setManageFiltersReady(true);
    }

    const handleAlertEventFiltersApply = (filters: IKpiAlertEventFilters) => {
        if (!manageFiltersBlock) {
            return;
        }
        const fields = structuredClone(manageFiltersBlock.component.settings.fields);
        // Reset filters that are not included in the alert event filters
        fields.forEach((f) => (f.savedValues = []));
        for (const [k, v] of Object.entries(filters)) {
            let value: string | number = v;
            const f = fields.find((f) => f.reference_name_escaped == k);
            if (f) {
                if (!Array.isArray(value)) {
                    if (f.numeric_type != null) {
                        value = Number(value);
                    }
                    f.savedValues = [value];
                } else {
                    f.savedValues = value;
                }
                // Duplicate value for range filter with single value, to make sure the filter is applied
                if (f.filterType == 'range' && f.savedValues.length == 1) {
                    f.savedValues.push(value);
                }
            }
        }
        actions.updateBlockSettings('manage_filters', {
            ...manageFiltersBlock.component.settings,
            //@ts-ignore
            fields: fields,
        });
    };

    const hasSpaceOnTop = (typeof manageFiltersBlock != 'undefined' && !contentSettings.forScreenShot) || hasUserMap;
    const content =
        contentSettings.context == 'edit' ? (
            <>
                {/* @ts-expect-error Server Component */}
                <DragDropContext onDragEnd={onDragEnd}>
                    {/* @ts-expect-error Server Component */}
                    <Droppable droppableId={'report_content_blocks'}>
                        {(provided: any) => (
                            <div ref={provided.innerRef}>
                                <ContentDropBox
                                    isSingle={filteredBlocks.length == 0}
                                    isIconDragging={isIconDragging}
                                    blockIndex={0}
                                    onAddNewBlock={onAddNewBlock}
                                    hasSpaceOnTop={hasSpaceOnTop}
                                />
                                {filteredBlocks.map((block, index) => (
                                    <Box key={block.uid}>
                                        {/* @ts-expect-error Server Component */}
                                        <Draggable draggableId={block.uid} key={block.uid} index={index}>
                                            {(provided: any) => (
                                                <div ref={provided.innerRef} {...provided.draggableProps}>
                                                    <Stack
                                                        direction={'row'}
                                                        justifyContent={'space-between'}
                                                        className={isIconDragging ? 'is-active-dragging' : ''}
                                                        sx={{
                                                            width: '100%',
                                                            '&.is-active-dragging': {
                                                                opacity: 0.3,
                                                                pointerEvents: 'none',
                                                            },
                                                        }}
                                                    >
                                                        <BlockContentArrow block={block} provided={provided} />
                                                        <Box sx={{ overflow: 'hidden', width: '100%' }}>
                                                            <ReportContentBlock
                                                                key={block.uid}
                                                                contentSettings={contentSettings}
                                                                block={block}
                                                                actions={actions}
                                                                datasetFields={datasetFields}
                                                            />
                                                        </Box>
                                                    </Stack>
                                                </div>
                                            )}
                                        </Draggable>
                                        <ContentDropBox
                                            isIconDragging={isIconDragging}
                                            blockIndex={index + 1}
                                            onAddNewBlock={onAddNewBlock}
                                        />
                                    </Box>
                                ))}
                            </div>
                        )}
                    </Droppable>
                </DragDropContext>
            </>
        ) : (
            <Stack spacing={2}>
                {filteredBlocks.map((block) => (
                    <ReportContentBlock
                        key={block.uid}
                        contentSettings={contentSettings}
                        block={block}
                        actions={actions}
                        datasetFields={datasetFields}
                    />
                ))}
            </Stack>
        );

    return (
        <Stack>
            {typeof manageFiltersBlock != 'undefined' && (
                <>
                    {!contentSettings.forScreenShot && blocksContext?.manageFiltersReady == true && (
                        <AlertRulesMenu
                            onApplyFilters={handleAlertEventFiltersApply}
                            contentSettings={contentSettings}
                        />
                    )}
                    <Box
                        className={contentSettings.forScreenShot ? 'd-none' : ''}
                        sx={{ mb: contentSettings.context == 'edit' ? undefined : 2 }}
                    >
                        <ReportContentBlock
                            key={'manage-filters'}
                            contentSettings={contentSettings}
                            block={manageFiltersBlock}
                            actions={actions}
                            isFilterBlock
                            datasetFields={datasetFields}
                        />
                    </Box>
                </>
            )}
            {hasUserMap && blocksContext?.manageFiltersReady == true && (
                <Box sx={{ textAlign: 'center' }}>
                    <ReactHookFormController
                        elementProps={prepareFormElementProps({
                            ...elementProps,
                            component: {
                                component: 'FormCheckbox',
                                name: 'restrict_data_with_user_map',
                                label: 'Restrict Data with User Map',
                            },
                        })}
                    />
                </Box>
            )}
            {blocksContext?.manageFiltersReady == true ? content : null}
        </Stack>
    );
}

function BlockContentArrow({ block, provided }: { block: BlockType<ComponentSettings>; provided: any }) {
    return (
        <Box
            sx={{
                border: '1px solid',
                borderRight: 'none',
                borderColor: (theme) => alpha(theme.palette.text.primary, 0.08),
                borderRadius: '1px 0 0 1px',
                width: '21px',
                flexShrink: 0,
                marginTop:
                    block.component.settings.is_collapsed_ind != 'Y' && block.component.settings?.title?.length
                        ? '32px'
                        : undefined,
            }}
        >
            <div style={{ padding: '8px 0 0 2px' }} {...provided.dragHandleProps}>
                <IconMi icon={'arrows-alt-v'} fontSize="16" />
            </div>
        </Box>
    );
}
